/* ==========================================================================
   walletService

   REAL MODE: reads the `wallets` table directly (filtered explicitly by
   estateContext.activeEstateId — see meterService's header comment for
   why an explicit filter matters once a super_admin's RLS grant spans
   every estate), but every write goes through the fund_wallet /
   withdraw_wallet Postgres functions in supabase/schema.sql instead of a
   plain update() — those functions check the balance and (for
   withdrawals) the caller's role and update the wallet + insert the
   transaction atomically. That's what makes "a user can't withdraw" a
   real rule: even a user who bypassed this file entirely and called
   withdraw_wallet() directly from the browser console would still be
   rejected by the function itself, not just by the Withdraw button being
   hidden. Both functions also take the target estate explicitly and
   independently re-check the caller may act on it — a super_admin can
   fund/withdraw any estate's wallet, everyone else only their own, same
   check happening twice (once for permission, once for which estate).

   fundWallet() still goes through paymentService first, same as mock mode
   — the wallet is only credited for real once that simulated (or, later,
   real the payment provider) payment confirms success.
   ========================================================================== */

import { config } from "../config";
import { getSupabase } from "./supabaseClient";
import { mapWalletRow } from "./supabaseMappers";
import { mockDb } from "./mock/mockDb";
import { delay, genReference, shouldSimulateFailure } from "./mock/mockUtils";
import { paymentService } from "./paymentService";
import { requireActiveEstateId } from "../state/estateContext";
import { sessionStore } from "../state/session";
import type { FundWalletRequest, FundWalletResult, WalletBalance, WithdrawRequest, WithdrawResult } from "../types";

async function getBalance(): Promise<WalletBalance> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(300);
    return {
      estateId,
      balanceKobo: mockDb.getWalletBalanceKobo(estateId),
      currency: "NGN",
      updatedAt: new Date().toISOString(),
    };
  }
  // Each person has their own wallet; it is looked up by who is signed in.
  const userId = sessionStore.user?.id;
  if (!userId) throw new Error("Please sign in again.");
  const { data, error } = await getSupabase().from("wallets").select("*").eq("user_id", userId).single();
  if (error || !data) throw new Error(error?.message ?? "Could not load wallet balance.");
  return mapWalletRow(data);
}

async function fundWallet(req: FundWalletRequest): Promise<FundWalletResult> {
  const estateId = requireActiveEstateId();

  // Mirrors the real sequence: initiate with the provider, wait for
  // verification, then credit the wallet only on confirmed success.
  const init = await paymentService.initiatePayment(req.amountKobo);
  const status = await paymentService.getPaymentStatus(init.paymentReference);

  if (status.status !== "success") {
    return {
      status: "failed",
      reference: init.paymentReference,
      amountKobo: req.amountKobo,
      failureReason: status.failureReason ?? "Payment could not be completed.",
    };
  }

  if (config.useMockServices) {
    const newBalance = mockDb.creditWallet(estateId, req.amountKobo);
    mockDb.addTransaction(estateId, {
      type: "wallet_funding",
      status: "success",
      amountKobo: req.amountKobo,
      direction: "credit",
      description: "Wallet Funding",
      reference: init.paymentReference,
    });
    return { status: "success", reference: init.paymentReference, amountKobo: req.amountKobo, newBalanceKobo: newBalance };
  }

  const { data, error } = await getSupabase().rpc("fund_wallet", {
    p_amount_kobo: req.amountKobo,
    p_reference: init.paymentReference,
    p_estate_id: estateId,
  });
  if (error) {
    return { status: "failed", reference: init.paymentReference, amountKobo: req.amountKobo, failureReason: error.message };
  }
  return {
    status: "success",
    reference: init.paymentReference,
    amountKobo: req.amountKobo,
    newBalanceKobo: (data as { balance_kobo: number }).balance_kobo,
  };
}

async function withdraw(req: WithdrawRequest): Promise<WithdrawResult> {
  const estateId = requireActiveEstateId();

  if (config.useMockServices) {
    await delay(800);
    const currentBalance = mockDb.getWalletBalanceKobo(estateId);

    if (shouldSimulateFailure()) {
      return {
        status: "failed",
        reference: genReference("WDR"),
        amountKobo: req.amountKobo,
        failureReason: "Withdrawal declined (simulated).",
      };
    }
    if (req.amountKobo > currentBalance) {
      return {
        status: "failed",
        reference: genReference("WDR"),
        amountKobo: req.amountKobo,
        failureReason: "Insufficient wallet balance.",
      };
    }

    const newBalance = mockDb.debitWallet(estateId, req.amountKobo);
    const reference = genReference("WDR");
    mockDb.addTransaction(estateId, {
      type: "wallet_withdrawal",
      status: "success",
      amountKobo: req.amountKobo,
      direction: "debit",
      description: "Wallet Withdrawal",
      reference,
    });

    return { status: "success", reference, amountKobo: req.amountKobo, newBalanceKobo: newBalance };
  }

  const reference = genReference("WDR");
  const { data, error } = await getSupabase().rpc("withdraw_wallet", {
    p_amount_kobo: req.amountKobo,
    p_reference: reference,
    p_estate_id: estateId,
  });
  if (error) {
    return { status: "failed", reference, amountKobo: req.amountKobo, failureReason: error.message };
  }
  return {
    status: "success",
    reference,
    amountKobo: req.amountKobo,
    newBalanceKobo: (data as { balance_kobo: number }).balance_kobo,
  };
}

/** REAL MODE: ask the "opay" Edge Function for a payment page. The person is then sent to OPay to pay;
 *  the wallet is credited only after OPay confirms (see checkTopup). */
async function startTopup(amountKobo: number): Promise<{ reference: string; cashierUrl: string }> {
  const { data, error } = await getSupabase().functions.invoke("opay", {
    body: { action: "create", amountKobo, origin: window.location.origin },
  });
  if (error) throw new Error("Couldn't reach the payment service. Please try again.");
  if (!data || data.status !== "ok" || !data.cashierUrl) {
    throw new Error((data && data.message) || "Couldn't start the payment. Please try again.");
  }
  return { reference: String(data.reference), cashierUrl: String(data.cashierUrl) };
}

export type TopupCheck = { status: "success" | "pending" | "failed" | "review"; amountKobo?: number };

/** Ask whether a top-up has been paid. Safe to call repeatedly. */
async function checkTopup(reference: string): Promise<TopupCheck> {
  const { data, error } = await getSupabase().functions.invoke("opay", { body: { action: "check", reference } });
  if (error || !data) return { status: "pending" };
  if (data.status === "success" || data.status === "failed" || data.status === "review") {
    return { status: data.status, amountKobo: data.amountKobo };
  }
  return { status: "pending", amountKobo: data.amountKobo };
}

export const walletService = { getBalance, fundWallet, withdraw, startTopup, checkTopup };
